import * as React from "react";
import { cn } from "@/lib/utils";

export type SubscriptionTier = "gold" | "silver" | "bronze";
type TierBadgeSize = "xs" | "sm" | "lg";

const TIER_STYLE: Record<
  SubscriptionTier,
  { short: string; long: string; bg: string; fg: string; ring: string }
> = {
  gold: { short: "זהב", long: "מנוי זהב", bg: "linear-gradient(135deg,#F6E9A9,#D4AF37)", fg: "#5c4a12", ring: "#D4AF37" },
  silver: { short: "כסף", long: "מנוי כסף", bg: "linear-gradient(135deg,#EDEEF0,#A8A9AD)", fg: "#3a3b3d", ring: "#A8A9AD" },
  bronze: { short: "ארד", long: "מנוי ארד", bg: "linear-gradient(135deg,#F1D8C0,#CD7F32)", fg: "#5a3618", ring: "#CD7F32" },
};

const SIZE: Record<TierBadgeSize, { pad: string; icon: number }> = {
  xs: { pad: "px-2 py-0.5 text-[11px] gap-1", icon: 11 },
  sm: { pad: "px-3 py-1 text-sm gap-1.5", icon: 14 },
  lg: { pad: "px-4 py-1.5 text-base gap-2", icon: 17 },
};

/** Tier-colored ring for an avatar (e.g. in the feed). */
export function tierRingColor(tier: SubscriptionTier): string {
  return TIER_STYLE[tier].ring;
}

function Crown({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 7l4 4 5-6 5 6 4-4v11H3z" />
    </svg>
  );
}

export interface TierBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tier: SubscriptionTier;
  size?: TierBadgeSize;
  /** Use the long label ("מנוי זהב") instead of the short one ("זהב"). */
  long?: boolean;
}

/**
 * Gold/silver/bronze membership badge. RTL, uses logical spacing. Driven by
 * `profiles.subscription_tier` (synced from the SY plan). Render nothing when the
 * caller has no tier by not mounting it (see callers).
 */
export const TierBadge = React.forwardRef<HTMLSpanElement, TierBadgeProps>(
  ({ tier, size = "sm", long = false, className, ...props }, ref) => {
    const s = TIER_STYLE[tier];
    const z = SIZE[size];
    return (
      <span
        ref={ref}
        dir="rtl"
        role="img"
        aria-label={s.long}
        className={cn(
          "inline-flex items-center rounded-full font-bold leading-none whitespace-nowrap shadow-sm",
          z.pad,
          className,
        )}
        style={{ background: s.bg, color: s.fg }}
        {...props}
      >
        <Crown size={z.icon} />
        {long ? s.long : s.short}
      </span>
    );
  },
);
TierBadge.displayName = "TierBadge";
